import * as THREE from 'three';

/* Generic premium sedan, built as a set of independently named parts.
   Coordinates: +x = vehicle right, +y = up, +z = vehicle front.
   Length ~5.0 m, width ~1.87 m, height ~1.46 m, wheelbase 3.0 m. */

const SQ = 0.45; // superellipse exponent — squared-off cross sections

const MAT = {
  paint: new THREE.MeshStandardMaterial({ name: 'paint_polar_white', color: 0xf2f3f1, metalness: 0.18, roughness: 0.38, side: THREE.DoubleSide }),
  glass: new THREE.MeshStandardMaterial({ name: 'glass_tinted', color: 0x11161a, metalness: 0.12, roughness: 0.04, transparent: true, opacity: 0.93, side: THREE.DoubleSide }),
  glassRoof: new THREE.MeshStandardMaterial({ name: 'glass_pano_roof', color: 0x0e1113, metalness: 0.15, roughness: 0.06, side: THREE.DoubleSide }),
  trim: new THREE.MeshStandardMaterial({ name: 'trim_graphite', color: 0x24272b, metalness: 0.2, roughness: 0.62, side: THREE.DoubleSide }),
  chrome: new THREE.MeshStandardMaterial({ name: 'chrome_bright', color: 0xe9edf0, metalness: 0.5, roughness: 0.12 }),
  lensFront: new THREE.MeshStandardMaterial({ name: 'lens_smoked_clear', color: 0x9aabb5, emissive: 0x4e626e, emissiveIntensity: 0.22, metalness: 0.3, roughness: 0.08, transparent: true, opacity: 0.95, side: THREE.DoubleSide }),
  lensCover: new THREE.MeshStandardMaterial({ name: 'lens_clear_cover', color: 0xbfd2dd, metalness: 0.35, roughness: 0.05, transparent: true, opacity: 0.20, side: THREE.DoubleSide }),
  housingGloss: new THREE.MeshStandardMaterial({ name: 'lamp_housing_black', color: 0x0b0d0f, metalness: 0.35, roughness: 0.26, side: THREE.DoubleSide }),
  drl: new THREE.MeshStandardMaterial({ name: 'led_drl_white', color: 0xf4fbff, emissive: 0xcfe8ff, emissiveIntensity: 1.5, roughness: 0.2, side: THREE.DoubleSide }),
  projGlow: new THREE.MeshStandardMaterial({ name: 'led_projector', color: 0xeaf4fb, emissive: 0xb9dcf5, emissiveIntensity: 1.1, roughness: 0.15 }),
  lensRear: new THREE.MeshStandardMaterial({ name: 'lens_red', color: 0xb01f1c, emissive: 0x8a1210, emissiveIntensity: 0.5, metalness: 0.2, roughness: 0.15, side: THREE.DoubleSide }),
  tire: new THREE.MeshStandardMaterial({ name: 'tire_rubber', color: 0x17191b, metalness: 0.0, roughness: 0.94 }),
  rim: new THREE.MeshStandardMaterial({ name: 'rim_alloy', color: 0xaeb3b9, metalness: 0.45, roughness: 0.2 }),
  shell: new THREE.MeshStandardMaterial({ name: 'shell_interior', color: 0x101215, metalness: 0.0, roughness: 0.95, side: THREE.DoubleSide }),
};

// [z, bottom y, top y, half-width]
const LOWER = [
  [2.485, 0.30, 0.66, 0.72],
  [2.455, 0.27, 0.72, 0.82],
  [2.400, 0.25, 0.775, 0.87],
  [2.260, 0.23, 0.825, 0.90],
  [2.050, 0.215, 0.865, 0.905],
  [1.700, 0.195, 0.935, 0.928],
  [1.200, 0.190, 1.000, 0.935],
  [0.600, 0.180, 1.035, 0.935],
  [-0.600, 0.180, 1.065, 0.935],
  [-1.200, 0.190, 1.085, 0.932],
  [-1.800, 0.205, 1.058, 0.915],
  [-2.150, 0.225, 1.025, 0.888],
  [-2.330, 0.25, 0.965, 0.845],
  [-2.420, 0.29, 0.885, 0.76],
  [-2.470, 0.36, 0.76, 0.50],
];
LOWER.sculpt = true;

const GREEN = [
  [1.280, 1.012, 1.030, 0.845],
  [1.000, 1.022, 1.150, 0.820],
  [0.700, 1.035, 1.290, 0.782],
  [0.400, 1.046, 1.395, 0.745],
  [0.100, 1.052, 1.438, 0.728],
  [-0.350, 1.058, 1.438, 0.728],
  [-0.750, 1.062, 1.405, 0.740],
  [-1.100, 1.058, 1.300, 0.756],
  [-1.380, 1.052, 1.165, 0.776],
  [-1.560, 1.045, 1.108, 0.792],
];

const smooth = (t) => t * t * (3 - 2 * t);

function crSeg(p0, p1, p2, p3, t) {
  const t2 = t * t, t3 = t2 * t;
  return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
}

function sample(keys, z) {
  if (z >= keys[0][0]) { const k = keys[0]; return { lo: k[1], hi: k[2], w: k[3] }; }
  const last = keys[keys.length - 1];
  if (z <= last[0]) return { lo: last[1], hi: last[2], w: last[3] };
  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i], b = keys[i + 1];
    if (z <= a[0] && z >= b[0]) {
      const t = (a[0] - z) / (a[0] - b[0]);
      const p0 = keys[Math.max(i - 1, 0)], p3 = keys[Math.min(i + 2, keys.length - 1)];
      return {
        lo: crSeg(p0[1], a[1], b[1], p3[1], t),
        hi: crSeg(p0[2], a[2], b[2], p3[2], t),
        w: crSeg(p0[3], a[3], b[3], p3[3], t),
      };
    }
  }
}

const g1 = (x, c, s) => Math.exp(-((x - c) * (x - c)) / (s * s));

function surface(keys, z, u, out) {
  const p = sample(keys, z);
  const yc = (p.lo + p.hi) / 2, h = (p.hi - p.lo) / 2;
  const t = u * Math.PI * 2, sx = Math.sin(t), sy = Math.cos(t);
  let x = p.w * Math.sign(sx) * Math.pow(Math.abs(sx), SQ);
  const y = yc + h * Math.sign(sy) * Math.pow(Math.abs(sy), SQ);
  if (keys.sculpt) {
    const yn = (y - p.lo) / (p.hi - p.lo);
    // shoulder swell, sharp swage-line crease, sill tuck
    x *= 1 + 0.026 * g1(yn, 0.60, 0.22) + 0.014 * g1(yn, 0.80, 0.055) - 0.030 * g1(yn, 0.05, 0.09);
  }
  out.set(x, y, z);
  return out;
}

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3(),
  _t1 = new THREE.Vector3(), _t2 = new THREE.Vector3();

function surfaceWithNormal(keys, z, u, target, normal) {
  surface(keys, z, u, target);
  surface(keys, z, u + 0.004, _a);
  surface(keys, z, u - 0.004, _b);
  _t1.subVectors(_a, _b);
  surface(keys, z + 0.01, u, _a);
  surface(keys, z - 0.01, u, _b);
  _t2.subVectors(_a, _b);
  normal.crossVectors(_t1, _t2).normalize();
  if (normal.dot(_c.set(target.x, target.y - 0.6, 0)) < 0) normal.negate();
  return target;
}

/** One panel: a patch of the body surface over a z-range x u-range,
 *  optionally pushed outward along the normal, optionally punched by holes. */
function panel(keys, opts) {
  const { z0, z1, u0, u1, segZ = 16, segU = 10, offset = 0, holes = [], name, material } = opts;
  const pos = [], nor = [], uv = [], idx = [];
  const p = new THREE.Vector3(), nv = new THREE.Vector3();
  for (let j = 0; j <= segZ; j++) {
    const z = z0 + ((z1 - z0) * j) / segZ;
    for (let i = 0; i <= segU; i++) {
      const u = u0 + ((u1 - u0) * i) / segU;
      surfaceWithNormal(keys, z, u, p, nv);
      pos.push(p.x + nv.x * offset, p.y + nv.y * offset, p.z + nv.z * offset);
      nor.push(nv.x, nv.y, nv.z);
      uv.push(i / segU, j / segZ);
    }
  }
  const row = segU + 1;
  const inHole = (a, b, cc, d) => {
    const z = (pos[a * 3 + 2] + pos[b * 3 + 2] + pos[cc * 3 + 2] + pos[d * 3 + 2]) / 4;
    const y = (pos[a * 3 + 1] + pos[b * 3 + 1] + pos[cc * 3 + 1] + pos[d * 3 + 1]) / 4;
    return holes.some((h) => (z - h.z) * (z - h.z) + (y - h.y) * (y - h.y) < h.r * h.r);
  };
  for (let j = 0; j < segZ; j++) {
    for (let i = 0; i < segU; i++) {
      const a = j * row + i, b = a + 1, c = a + row, d = c + 1;
      if (holes.length && inHole(a, b, c, d)) continue;
      idx.push(a, c, b, b, c, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  const mesh = new THREE.Mesh(g, material);
  mesh.name = name;
  return mesh;
}

/** Flat fan cap closing an open end of the lofted body. */
function endCap(keys, z, name, material) {
  const ring = 44, p = new THREE.Vector3();
  const pos = [], nor = [], uv = [], idx = [];
  const c = sample(keys, z), yc = (c.lo + c.hi) / 2;
  const nz = Math.sign(z);
  pos.push(0, yc, z); nor.push(0, 0, nz); uv.push(0.5, 0.5);
  for (let i = 0; i <= ring; i++) {
    const u = -0.5 + i / ring;
    surface(keys, z, u, p);
    pos.push(p.x, p.y, z); nor.push(0, 0, nz); uv.push(0.5 + p.x, 0.5 + p.y);
  }
  for (let i = 1; i <= ring; i++) idx.push(0, nz > 0 ? i : i + 1, nz > 0 ? i + 1 : i);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  const m = new THREE.Mesh(g, material);
  m.name = name;
  return m;
}

const FRONT_AXLE = 1.58, REAR_AXLE = -1.42, WHEEL_R = 0.36, ARCH_R = 0.49;
const frontArch = { z: FRONT_AXLE, y: WHEEL_R, r: ARCH_R };
const rearArch = { z: REAR_AXLE, y: WHEEL_R, r: ARCH_R };

function archLip(side, axleZ, name) {
  const g = new THREE.TorusGeometry(ARCH_R, 0.026, 8, 30, Math.PI);
  g.rotateY(Math.PI / 2);
  const m = new THREE.Mesh(g, MAT.paint);
  m.position.set(side * 0.916, WHEEL_R, axleZ);
  m.name = name;
  return m;
}

function wheel(side, axleZ, id) {
  const g = new THREE.Group();
  g.name = id;
  const lathe = (pts, mat, nm, seg = 48) => {
    const m = new THREE.Mesh(new THREE.LatheGeometry(pts.map((p) => new THREE.Vector2(p[0], p[1])), seg), mat);
    m.rotation.z = Math.PI / 2;
    m.name = nm;
    return m;
  };
  const R = WHEEL_R;
  // tire with rounded shoulders
  g.add(lathe([[R - 0.075, -0.127], [R - 0.018, -0.115], [R, -0.058], [R, 0.058], [R - 0.018, 0.115], [R - 0.075, 0.127]], MAT.tire, id + '_tire'));
  // dark barrel behind the spokes
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.287, 0.287, 0.17, 36, 1, true), MAT.trim);
  barrel.rotation.z = Math.PI / 2;
  barrel.name = id + '_barrel';
  g.add(barrel);
  // rim: shallow dished face
  const dish = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.19, 0.028, 40), MAT.trim);
  dish.rotation.z = Math.PI / 2;
  dish.position.x = side * 0.058;
  dish.name = id + '_rim';
  g.add(dish);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.35;
    for (const k of [-1, 1]) {
      const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.25, 0.04), MAT.rim);
      spoke.position.set(
        side * 0.075,
        Math.cos(a) * 0.155 - Math.sin(a) * k * 0.034,
        Math.sin(a) * 0.155 + Math.cos(a) * k * 0.034
      );
      spoke.rotation.x = a;
      spoke.name = id + '_spoke_' + (i + 1) + (k > 0 ? 'b' : 'a');
      g.add(spoke);
    }
  }
  const lip = new THREE.Mesh(new THREE.TorusGeometry(0.272, 0.013, 8, 40), MAT.rim);
  lip.rotation.y = Math.PI / 2;
  lip.position.x = side * 0.09;
  lip.name = id + '_rim_lip';
  g.add(lip);
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.045, 24), MAT.chrome);
  hub.rotation.z = Math.PI / 2;
  hub.position.x = side * 0.085;
  hub.name = id + '_hub';
  g.add(hub);
  const brake = new THREE.Mesh(new THREE.CylinderGeometry(0.165, 0.165, 0.025, 28), MAT.chrome);
  brake.rotation.z = Math.PI / 2;
  brake.position.x = -side * 0.015;
  brake.name = id + '_brake_disc';
  g.add(brake);
  g.position.set(side * 0.815, WHEEL_R, axleZ);
  return g;
}

function mirror(side, id) {
  const g = new THREE.Group();
  g.name = id;
  const base = new THREE.Vector3(), n = new THREE.Vector3();
  surfaceWithNormal(LOWER, 0.72, side * 0.14, base, n); // door shoulder at mirror station
  const arm = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.024, 0.045), MAT.trim);
  arm.position.set(base.x + side * 0.02, base.y + 0.045, 0.70);
  arm.rotation.z = side * -0.12;
  arm.name = id + '_arm';
  const shell = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.09, 0.21), MAT.paint);
  shell.position.set(base.x + side * 0.095, base.y + 0.075, 0.70);
  shell.rotation.y = side * 0.08;
  shell.name = id + '_housing';
  const glass = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.068, 0.165), MAT.glass);
  glass.position.set(base.x + side * 0.136, base.y + 0.075, 0.705);
  glass.name = id + '_glass';
  g.add(arm, shell, glass);
  return g;
}

/** Rectangular LED module flush with the body surface at (z, u). */
function ledModule(side, z, u, w, nm) {
  const g = new THREE.Group();
  g.name = nm;
  const p = new THREE.Vector3(), n = new THREE.Vector3();
  surfaceWithNormal(LOWER, z, side * u, p, n);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
  const bezel = new THREE.Mesh(new THREE.BoxGeometry(w, w * 0.56, 0.028), MAT.trim);
  bezel.quaternion.copy(q);
  bezel.position.copy(p).addScaledVector(n, -0.004);
  bezel.name = nm + '_bezel';
  const face = new THREE.Mesh(new THREE.BoxGeometry(w * 0.74, w * 0.36, 0.034), MAT.projGlow);
  face.quaternion.copy(q);
  face.position.copy(p).addScaledVector(n, -0.001);
  face.name = nm + '_led';
  g.add(bezel, face);
  return g;
}

/** Builds the vehicle. Returns { model, parts } where each part is a
 *  named group carrying userData.partId. */
export function buildVehicle() {
  const model = new THREE.Group();
  model.name = 'vehicle';
  const parts = [];

  const part = (id, label, region, children) => {
    const g = new THREE.Group();
    g.name = 'vehicle_' + id;
    g.userData.partId = id;
    children.forEach((c) => c && g.add(c));
    model.add(g);
    parts.push({ id, label, region, node: g, name: g.name });
    return g;
  };

  // Dark inner shell so panel gaps and wheel wells read as shadow, not sky.
  const coreKeys = LOWER.map(([z, lo, hi, w]) => [z, lo + 0.02, hi - 0.02, w * 0.93]);
  const core = panel(coreKeys, { z0: -2.44, z1: 2.46, u0: -0.5, u1: 0.5, segZ: 40, segU: 30, name: 'vehicle_inner_shell', material: MAT.shell });
  const cabinCore = panel(GREEN.map(([z, lo, hi, w]) => [z, lo, hi - 0.015, w * 0.94]), { z0: -1.55, z1: 1.05, u0: -0.46, u1: 0.46, segZ: 30, segU: 22, name: 'vehicle_cabin_shell', material: MAT.shell });
  const floor = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.05, 4.1), MAT.trim);
  floor.position.set(0, 0.19, 0);
  floor.name = 'vehicle_floor_pan';
  model.add(core, cabinCore, floor);
  // painted shoulder deck: covers the body top strip around the glasshouse base
  const deck = panel(LOWER, { z0: -1.56, z1: 1.272, u0: -0.135, u1: 0.135, segZ: 30, segU: 12, offset: -0.002, name: 'vehicle_shoulder_deck', material: MAT.paint });
  model.add(deck);
  // chrome belt-line trim along the base of the glasshouse
  for (const side of [1, -1]) {
    const strip = panel(GREEN, { z0: -1.545, z1: 1.05, u0: side * 0.42, u1: side * 0.499, segZ: 26, segU: 4, offset: 0.006, name: 'vehicle_' + (side > 0 ? 'right' : 'left') + '_belt_trim', material: MAT.chrome });
    model.add(strip);
    const rocker = panel(LOWER, { z0: -1.22, z1: 1.22, u0: side * 0.400, u1: side * 0.414, segZ: 20, segU: 2, offset: 0.007, holes: [frontArch, rearArch], name: 'vehicle_' + (side > 0 ? 'right' : 'left') + '_rocker_trim', material: MAT.chrome });
    model.add(rocker);
  }

  const S = { u0: 0.135, u1: 0.345 };

  // ---- Front ----
  part('front_bumper', 'Front bumper', 'Front', [
    panel(LOWER, { z0: 2.335, z1: 2.485, u0: -0.5, u1: 0.5, segZ: 12, segU: 48, name: 'vehicle_front_bumper', material: MAT.paint }),
    endCap(LOWER, 2.478, 'vehicle_front_bumper_face', MAT.paint),
    (() => {
      const g = new THREE.Group();
      const m = new THREE.Mesh(new THREE.BoxGeometry(1.50, 0.155, 0.05), MAT.housingGloss);
      m.position.set(0, 0.40, 2.468);
      m.rotation.x = 0.08;
      m.name = 'vehicle_front_bumper_lower_intake';
      g.add(m);
      for (let i = 0; i < 3; i++) {
        const sl = new THREE.Mesh(new THREE.BoxGeometry(1.46, 0.012, 0.04), MAT.trim);
        sl.position.set(0, 0.355 + i * 0.045, 2.484);
        sl.name = 'vehicle_front_intake_mesh_' + (i + 1);
        g.add(sl);
      }
      const lip = new THREE.Mesh(new THREE.BoxGeometry(1.58, 0.036, 0.10), MAT.trim);
      lip.position.set(0, 0.30, 2.452);
      lip.name = 'vehicle_front_splitter_lip';
      g.add(lip);
      return g;
    })(),
  ]);

  part('front_grille', 'Front grille', 'Front', [
    (() => {
      const g = new THREE.Group();
      const gg = new THREE.Group();
      gg.position.set(0, 0.63, 2.462);
      gg.rotation.x = 0.08;
      const back = new THREE.Mesh(new THREE.BoxGeometry(1.12, 0.26, 0.04), MAT.housingGloss);
      back.name = 'vehicle_front_grille';
      gg.add(back);
      for (let i = 0; i < 4; i++) {
        const slat = new THREE.Mesh(new THREE.BoxGeometry(1.00 + i * 0.032, 0.042, 0.06), MAT.chrome);
        slat.position.set(0, -0.087 + i * 0.058, 0.014);
        slat.name = 'vehicle_front_grille_louver_' + (i + 1);
        gg.add(slat);
      }
      const fr = (w, h, x, y, nm) => {
        const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.05), MAT.chrome);
        m.position.set(x, y, 0.008);
        m.name = nm;
        return m;
      };
      gg.add(fr(1.16, 0.020, 0, 0.135, 'vehicle_front_grille_frame_top'));
      gg.add(fr(1.08, 0.020, 0, -0.135, 'vehicle_front_grille_frame_bottom'));
      const sfr = fr(0.020, 0.29, 0.565, 0, 'vehicle_front_grille_frame_right');
      sfr.rotation.z = -0.14;
      gg.add(sfr);
      const sfl = fr(0.020, 0.29, -0.565, 0, 'vehicle_front_grille_frame_left');
      sfl.rotation.z = 0.14;
      gg.add(sfl);
      g.add(gg);
      return g;
    })(),
  ]);

  for (const side of [1, -1]) {
    const s = side > 0 ? 'right' : 'left';
    part(`front_${s}_headlight`, `Front ${s} headlight`, 'Front', [
      panel(LOWER, { z0: 2.30, z1: 2.462, u0: side * 0.125, u1: side * 0.185, segZ: 8, segU: 6, offset: 0.008, name: `vehicle_front_${s}_headlight_housing`, material: MAT.housingGloss }),
      panel(LOWER, { z0: 2.32, z1: 2.456, u0: side * 0.128, u1: side * 0.140, segZ: 8, segU: 2, offset: 0.022, name: `vehicle_front_${s}_headlight_drl_blade`, material: MAT.drl }),
      panel(LOWER, { z0: 2.30, z1: 2.335, u0: side * 0.140, u1: side * 0.180, segZ: 3, segU: 4, offset: 0.022, name: `vehicle_front_${s}_headlight_drl_hook`, material: MAT.drl }),
      ledModule(side, 2.415, 0.155, 0.075, `vehicle_front_${s}_led_projector_inner`),
      ledModule(side, 2.362, 0.172, 0.075, `vehicle_front_${s}_led_projector_outer`),
      panel(LOWER, { z0: 2.302, z1: 2.460, u0: side * 0.126, u1: side * 0.183, segZ: 8, segU: 6, offset: 0.030, name: `vehicle_front_${s}_headlight_lens`, material: MAT.lensCover }),
    ]);
  }

  part('hood', 'Hood', 'Front', [
    panel(LOWER, { z0: 1.275, z1: 2.345, u0: -0.132, u1: 0.132, segZ: 28, segU: 16, name: 'vehicle_hood', material: MAT.paint }),
    panel(LOWER, { z0: 1.32, z1: 2.28, u0: 0.052, u1: 0.070, segZ: 20, segU: 2, offset: 0.006, name: 'vehicle_hood_char_line_right', material: MAT.paint }),
    panel(LOWER, { z0: 1.32, z1: 2.28, u0: -0.070, u1: -0.052, segZ: 20, segU: 2, offset: 0.006, name: 'vehicle_hood_char_line_left', material: MAT.paint }),
    panel(GREEN, { z0: 1.04, z1: 1.279, u0: -0.165, u1: 0.165, segZ: 10, segU: 14, offset: -0.004, name: 'vehicle_cowl_panel', material: MAT.paint }),
  ]);

  for (const side of [1, -1]) {
    const s = side > 0 ? 'right' : 'left';
    const fog = new THREE.Group();
    fog.name = `vehicle_front_${s}_fog_lamp`;
    const p = new THREE.Vector3(), n = new THREE.Vector3();
    surfaceWithNormal(LOWER, 2.40, side * 0.285, p, n);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
    const pocket = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.105, 0.045), MAT.housingGloss);
    pocket.quaternion.copy(q);
    pocket.position.copy(p).addScaledVector(n, -0.006);
    pocket.name = `vehicle_front_${s}_fog_pocket`;
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.27, 0.016, 0.03), MAT.chrome);
    blade.quaternion.copy(q);
    blade.rotateZ(side * -0.06);
    blade.position.copy(p).addScaledVector(n, 0.012);
    blade.position.y += 0.062;
    blade.name = `vehicle_front_${s}_fog_chrome_blade`;
    const lens = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.022, 0.02), MAT.projGlow);
    lens.quaternion.copy(q);
    lens.position.copy(p).addScaledVector(n, 0.010);
    lens.name = `vehicle_front_${s}_fog_led`;
    fog.add(pocket, blade, lens);
    part(`front_${s}_fog_lamp`, `Front ${s} fog lamp`, 'Front', [fog]);
  }

  for (const side of [1, -1]) {
    const s = side > 0 ? 'right' : 'left';
    part(`front_${s}_fender`, `Front ${s} fender`, 'Front', [
      panel(LOWER, { z0: 1.165, z1: 2.345, u0: side * S.u0, u1: side * S.u1, segZ: 32, segU: 18, holes: [frontArch], name: `vehicle_front_${s}_fender`, material: MAT.paint }),
      archLip(side, FRONT_AXLE, `vehicle_front_${s}_arch`),
    ]);
  }

  // ---- Sides ----
  for (const side of [1, -1]) {
    const s = side > 0 ? 'right' : 'left';
    const handle = (z, nm) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.026, 0.19), MAT.chrome);
      m.position.set(side * 0.955, 0.885, z);
      m.name = nm;
      return m;
    };
    part(`front_${s}_door`, `Front ${s} door`, 'Sides', [
      panel(LOWER, { z0: 0.0, z1: 1.155, u0: side * S.u0, u1: side * S.u1, segZ: 26, segU: 18, holes: [frontArch], name: `vehicle_front_${s}_door`, material: MAT.paint }),
      handle(0.62, `vehicle_front_${s}_door_handle`),
    ]);
    part(`rear_${s}_door`, `Rear ${s} door`, 'Sides', [
      panel(LOWER, { z0: -1.165, z1: -0.01, u0: side * S.u0, u1: side * S.u1, segZ: 26, segU: 18, holes: [rearArch], name: `vehicle_rear_${s}_door`, material: MAT.paint }),
      handle(-0.42, `vehicle_rear_${s}_door_handle`),
    ]);
    part(`rear_${s}_fender`, `Rear ${s} fender`, 'Sides', [
      panel(LOWER, { z0: -2.345, z1: -1.175, u0: side * S.u0, u1: side * S.u1, segZ: 30, segU: 18, holes: [rearArch], name: `vehicle_rear_${s}_fender`, material: MAT.paint }),
      archLip(side, REAR_AXLE, `vehicle_rear_${s}_arch`),
    ]);
    part(`${s}_side_skirt`, `${s[0].toUpperCase() + s.slice(1)} side skirt`, 'Sides', [
      panel(LOWER, { z0: -1.30, z1: 1.28, u0: side * 0.345, u1: side * 0.44, segZ: 42, segU: 8, holes: [frontArch, rearArch], name: `vehicle_${s}_side_skirt`, material: MAT.paint }),
    ]);
    part(`${s}_side_mirror`, `${s[0].toUpperCase() + s.slice(1)} side mirror`, 'Sides', [mirror(side, `vehicle_${s}_side_mirror`)]);
  }

  // ---- Rear ----
  part('rear_bumper', 'Rear bumper', 'Rear', [
    panel(LOWER, { z0: -2.47, z1: -2.345, u0: -0.5, u1: 0.5, segZ: 12, segU: 48, name: 'vehicle_rear_bumper', material: MAT.paint }),
    endCap(LOWER, -2.4635, 'vehicle_rear_bumper_face', MAT.paint),
    (() => {
      const g = new THREE.Group();
      const val = new THREE.Mesh(new THREE.BoxGeometry(1.34, 0.07, 0.06), MAT.trim);
      val.position.set(0, 0.38, -2.462);
      val.name = 'vehicle_rear_valance';
      g.add(val);
      for (const sd of [1, -1]) {
        const nm = sd > 0 ? 'right' : 'left';
        const tip = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.055, 0.05), MAT.chrome);
        tip.position.set(sd * 0.545, 0.383, -2.475);
        tip.name = 'vehicle_exhaust_tip_' + nm;
        g.add(tip);
        const inner = new THREE.Mesh(new THREE.BoxGeometry(0.135, 0.03, 0.054), MAT.shell);
        inner.position.set(sd * 0.545, 0.383, -2.478);
        inner.name = 'vehicle_exhaust_inner_' + nm;
        g.add(inner);
      }
      return g;
    })(),
  ]);

  part('trunk', 'Trunk lid', 'Rear', [
    panel(LOWER, { z0: -2.355, z1: -1.175, u0: -0.132, u1: 0.132, segZ: 30, segU: 16, name: 'vehicle_trunk', material: MAT.paint }),
    (() => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.022, 0.03), MAT.chrome);
      m.position.set(0, 0.86, -2.408);
      m.name = 'vehicle_trunk_chrome_bar';
      return m;
    })(),
    (() => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(1.04, 0.02, 0.095), MAT.paint);
      m.position.set(0, 0.985, -2.312);
      m.rotation.x = -0.10;
      m.name = 'vehicle_trunk_lip_spoiler';
      return m;
    })(),
  ]);

  for (const side of [1, -1]) {
    const s = side > 0 ? 'right' : 'left';
    part(`rear_${s}_tail_light`, `Rear ${s} tail light`, 'Rear', [
      (() => {
        const tl = new THREE.Group();
        tl.position.set(side * 0.235, 0.665, -2.462);
        tl.rotation.x = 0.10;
        const hb = new THREE.Mesh(new THREE.BoxGeometry(0.47, 0.10, 0.03), MAT.housingGloss);
        hb.name = `vehicle_rear_${s}_tail_light_housing`;
        tl.add(hb);
        const lens = new THREE.Mesh(new THREE.BoxGeometry(0.462, 0.072, 0.034), MAT.lensRear);
        lens.name = `vehicle_rear_${s}_tail_light`;
        tl.add(lens);
        const led = new THREE.Mesh(new THREE.BoxGeometry(0.462, 0.018, 0.038), MAT.drl);
        led.position.set(0, 0.026, 0);
        led.name = `vehicle_rear_${s}_tail_light_led`;
        tl.add(led);
        const cap = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.096, 0.032), MAT.lensRear);
        cap.position.set(side * 0.29, -0.004, 0.052);
        cap.rotation.y = side * -0.62;
        cap.name = `vehicle_rear_${s}_tail_light_corner`;
        tl.add(cap);
        return tl;
      })(),
    ]);
  }

  // ---- Upper body / glass ----
  part('windshield', 'Windshield', 'Upper body', [
    panel(GREEN, { z0: 0.60, z1: 1.21, u0: -0.155, u1: 0.155, segZ: 18, segU: 14, name: 'vehicle_windshield', material: MAT.glass }),
  ]);
  part('roof', 'Roof', 'Upper body', [
    panel(GREEN, { z0: -0.80, z1: 0.60, u0: -0.17, u1: 0.17, segZ: 26, segU: 16, name: 'vehicle_roof', material: MAT.glassRoof }),
  ]);
  part('rear_windshield', 'Rear windshield', 'Upper body', [
    panel(GREEN, { z0: -1.44, z1: -0.81, u0: -0.155, u1: 0.155, segZ: 16, segU: 14, name: 'vehicle_rear_windshield', material: MAT.glass }),
  ]);

  for (const side of [1, -1]) {
    const s = side > 0 ? 'right' : 'left';
    part(`${s}_a_pillar`, `${s[0].toUpperCase() + s.slice(1)} A-pillar`, 'Upper body', [
      panel(GREEN, { z0: 0.60, z1: 1.21, u0: side * 0.155, u1: side * 0.45, segZ: 14, segU: 12, name: `vehicle_${s}_a_pillar`, material: MAT.paint }),
    ]);
    part(`front_${s}_window`, `Front ${s} window`, 'Upper body', [
      panel(GREEN, { z0: 0.01, z1: 0.60, u0: side * 0.17, u1: side * 0.45, segZ: 14, segU: 12, name: `vehicle_front_${s}_window`, material: MAT.glass }),
    ]);
    part(`${s}_b_pillar`, `${s[0].toUpperCase() + s.slice(1)} B-pillar`, 'Upper body', [
      panel(GREEN, { z0: -0.085, z1: 0.0, u0: side * 0.17, u1: side * 0.45, segZ: 4, segU: 12, name: `vehicle_${s}_b_pillar`, material: MAT.trim }),
    ]);
    part(`rear_${s}_window`, `Rear ${s} window`, 'Upper body', [
      panel(GREEN, { z0: -0.86, z1: -0.095, u0: side * 0.17, u1: side * 0.45, segZ: 16, segU: 12, name: `vehicle_rear_${s}_window`, material: MAT.glass }),
    ]);
    part(`${s}_c_pillar`, `${s[0].toUpperCase() + s.slice(1)} C-pillar`, 'Upper body', [
      panel(GREEN, { z0: -1.575, z1: -0.875, u0: side * 0.178, u1: side * 0.45, segZ: 18, segU: 12, name: `vehicle_${s}_c_pillar`, material: MAT.paint }),
      panel(GREEN, { z0: -1.578, z1: -1.44, u0: side * 0.0, u1: side * 0.185, segZ: 6, segU: 8, name: `vehicle_${s}_c_pillar_base`, material: MAT.paint }),
    ]);
    part(`rear_${s}_quarter_glass`, `Rear ${s} quarter glass`, 'Upper body', [
      panel(GREEN, { z0: -1.05, z1: -0.875, u0: side * 0.19, u1: side * 0.36, segZ: 8, segU: 8, offset: 0.004, name: `vehicle_rear_${s}_quarter_glass`, material: MAT.glass }),
    ]);
  }

  // ---- CamCom taxonomy additions ----
  for (const side of [1, -1]) {
    const s = side > 0 ? 'right' : 'left';
    part(`${s}_cladding`, `${s[0].toUpperCase() + s.slice(1)} cladding`, 'Sides', [
      panel(LOWER, { z0: -1.165, z1: 1.155, u0: side * 0.30, u1: side * 0.345, segZ: 30, segU: 4, offset: 0.008, holes: [frontArch, rearArch], name: `vehicle_${s}_cladding`, material: MAT.trim }),
    ]);
    // fog lamp cowl: trim plate framing the fog pocket
    const cp = new THREE.Vector3(), cn = new THREE.Vector3();
    surfaceWithNormal(LOWER, 2.40, side * 0.285, cp, cn);
    const cq = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), cn);
    const cowl = new THREE.Mesh(new THREE.BoxGeometry(0.315, 0.15, 0.018), MAT.trim);
    cowl.quaternion.copy(cq);
    cowl.position.copy(cp).addScaledVector(cn, -0.012);
    cowl.name = `vehicle_${s}_fog_lamp_cowl`;
    part(`${s}_fog_lamp_cowl`, `${s[0].toUpperCase() + s.slice(1)} fog lamp cowl`, 'Front', [cowl]);
    // rear fog lamp: red module low on the rear bumper corner
    const rp = new THREE.Vector3(), rn = new THREE.Vector3();
    surfaceWithNormal(LOWER, -2.415, side * 0.30, rp, rn);
    const rq = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), rn);
    const rbez = new THREE.Mesh(new THREE.BoxGeometry(0.20, 0.07, 0.02), MAT.trim);
    rbez.quaternion.copy(rq); rbez.position.copy(rp).addScaledVector(rn, -0.004);
    rbez.name = `vehicle_rear_${s}_fog_bezel`;
    const rlens = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.045, 0.024), MAT.lensRear);
    rlens.quaternion.copy(rq); rlens.position.copy(rp).addScaledVector(rn, 0.002);
    rlens.name = `vehicle_rear_${s}_fog_lens`;
    part(`rear_${s}_fog_lamp`, `Rear ${s} fog lamp`, 'Rear', [rbez, rlens]);
  }
  {
    const bez = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.07, 0.016), MAT.trim);
    bez.position.set(0, 0.44, -2.468); bez.name = 'vehicle_center_rear_fog_bezel';
    const lens = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.045, 0.02), MAT.lensRear);
    lens.position.set(0, 0.44, -2.476); lens.name = 'vehicle_center_rear_fog_lens';
    part('center_rear_fog_lamp', 'Center rear fog lamp', 'Rear', [bez, lens]);
  }
  {
    // fuel cap on the left rear quarter
    const p = new THREE.Vector3(), n = new THREE.Vector3();
    surfaceWithNormal(LOWER, -1.62, -0.185, p, n);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
    const lidGeo = new THREE.CylinderGeometry(0.075, 0.075, 0.01, 28);
    lidGeo.rotateX(Math.PI / 2);
    const lid = new THREE.Mesh(lidGeo, MAT.paint);
    lid.quaternion.copy(q); lid.position.copy(p).addScaledVector(n, 0.004);
    lid.name = 'vehicle_fuel_cap';
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.075, 0.006, 8, 28), MAT.trim);
    ring.quaternion.copy(q); ring.position.copy(p).addScaledVector(n, 0.006);
    ring.name = 'vehicle_fuel_cap_ring';
    part('cap', 'Cap (fuel)', 'Sides', [lid, ring]);
  }
  part('spoiler', 'Spoiler', 'Rear', [
    panel(LOWER, { z0: -2.43, z1: -2.33, u0: -0.125, u1: 0.125, segZ: 6, segU: 12, offset: 0.020, name: 'vehicle_spoiler', material: MAT.paint }),
  ]);
  part('spoiler_light', 'Spoiler light', 'Rear', [
    panel(LOWER, { z0: -2.405, z1: -2.362, u0: -0.075, u1: 0.075, segZ: 3, segU: 8, offset: 0.030, name: 'vehicle_spoiler_light', material: MAT.lensRear }),
  ]);
  part('inside_roof', 'Inside roof', 'Roof', [
    panel(GREEN, { z0: -0.78, z1: 0.58, u0: -0.155, u1: 0.155, segZ: 16, segU: 10, offset: -0.035, name: 'vehicle_inside_roof', material: MAT.trim }),
  ]);
  // glass blanks: body-color close-out panels, hidden until selected
  const BLANKS = [
    ['front_windshield_blank', { z0: 0.60, z1: 1.21, u0: -0.155, u1: 0.155, segZ: 12, segU: 10 }],
    ['rear_windshield_blank', { z0: -1.44, z1: -0.81, u0: -0.155, u1: 0.155, segZ: 10, segU: 10 }],
    ['left_front_window_blank', { z0: 0.01, z1: 0.60, u0: -0.17, u1: -0.45, segZ: 10, segU: 8 }],
    ['right_front_window_blank', { z0: 0.01, z1: 0.60, u0: 0.17, u1: 0.45, segZ: 10, segU: 8 }],
    ['left_rear_window_blank', { z0: -0.86, z1: -0.095, u0: -0.17, u1: -0.45, segZ: 10, segU: 8 }],
    ['right_rear_window_blank', { z0: -0.86, z1: -0.095, u0: 0.17, u1: 0.45, segZ: 10, segU: 8 }],
    ['left_quarter_glass_blank', { z0: -1.05, z1: -0.875, u0: -0.19, u1: -0.36, segZ: 6, segU: 6 }],
    ['right_quarter_glass_blank', { z0: -1.05, z1: -0.875, u0: 0.19, u1: 0.36, segZ: 6, segU: 6 }],
  ];
  for (const [bid, o] of BLANKS) {
    const g = part(bid, bid.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), 'Glass', [
      panel(GREEN, { ...o, offset: 0.006, name: 'vehicle_' + bid, material: MAT.paint }),
    ]);
    g.visible = false;
    parts[parts.length - 1].blank = true;
  }

  // ---- Wheels ----
  part('front_right_wheel', 'Front right wheel', 'Wheels', [wheel(1, FRONT_AXLE, 'vehicle_front_right_wheel')]);
  part('front_left_wheel', 'Front left wheel', 'Wheels', [wheel(-1, FRONT_AXLE, 'vehicle_front_left_wheel')]);
  part('rear_right_wheel', 'Rear right wheel', 'Wheels', [wheel(1, REAR_AXLE, 'vehicle_rear_right_wheel')]);
  part('rear_left_wheel', 'Rear left wheel', 'Wheels', [wheel(-1, REAR_AXLE, 'vehicle_rear_left_wheel')]);

  parts.forEach((p) => {
    p.node.traverse((o) => {
      if (o.isMesh) o.userData.baseMaterial = o.material;
    });
  });

  return { model, parts, materials: MAT };
}
